import { useMemo, type JSX } from 'react';
import katex from 'katex';

/**
 * Mathematical notation.
 *
 * All mathematics on the platform renders through here, from LaTeX source
 * (§9) — never from images, so it stays selectable, searchable, screen-readable
 * and sharp at any zoom.
 */

type MathProps = {
  /** LaTeX source, without delimiters. */
  latex: string;
  /** Display mode centres the expression on its own line. */
  display?: boolean;
  className?: string;
  /** Read aloud by screen readers in place of the notation. */
  ariaLabel?: string;
};

export function Math({ latex, display = false, className, ariaLabel }: MathProps): JSX.Element {
  const html = useMemo(() => {
    try {
      return katex.renderToString(latex, {
        displayMode: display,
        throwOnError: false,
        // Content is authored in-repo, but rendering untrusted strings should
        // never be able to inject markup.
        trust: false,
        strict: false,
        output: 'html',
      });
    } catch {
      return '';
    }
  }, [latex, display]);

  if (html === '') {
    // Falling back to the source is better than showing nothing: the student
    // still sees the mathematics, even if it is unrendered.
    return <code className={className}>{latex}</code>;
  }

  return (
    <span
      className={className}
      role="math"
      aria-label={ariaLabel ?? latex}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/** Block-level mathematics, centred on its own line. */
export function MathBlock({ latex, caption }: { latex: string; caption?: string }): JSX.Element {
  return (
    <figure className="math-block">
      <Math latex={latex} display />
      {caption ? <figcaption>{caption}</figcaption> : null}
    </figure>
  );
}
